import { makeAutoObservable, runInAction } from 'mobx';
import type { GetProductsUseCase } from '@/core/application/use-cases/product/GetProductsUseCase';
import type { GetPaymentPlansUseCase } from '@/core/application/use-cases/payment/GetPaymentPlansUseCase';
import type { ApiProductRepository } from '@/core/infrastructure/repositories/ApiProductRepository';
import type {
  PSSDurationOption,
  PSSPlansResponse,
} from '@/core/infrastructure/services/ApiPaymentService';
import {
  ApiCategoriesResponse,
  ApiFiltersResponse,
  ApiHomepageCollections,
  MarketplaceAnnouncementStrip,
  MarketplaceHeroBanner,
  MarketplaceShowcaseCard,
  ProgramRewardOffer,
  Brand,
  PaymentOption,
  Product,
  ProductFilters,
} from '@/core/domain/entities/Product';
import {
  ProductFiltersRequest,
  ProductSearchRequest,
} from '@/core/application/dtos/ProductDto';

export interface ProductStoreApiError {
  message: string;
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
}

interface HttpErrorLike {
  message?: string;
  response?: {
    status?: number;
    data?: {
      message?: string;
      code?: string;
      details?: Record<string, unknown>;
    };
  };
}

interface HomeContentLoaderResponse<T> {
  results?: T[];
}

const MAX_MARKETPLACE_HOME_CONTENT_RETRIES = 5;
const MAX_PROGRAM_REWARD_OFFER_RETRIES = 3;

export class ProductStore {
  // State
  products: Product[] = [];
  featuredProducts: Product[] = [];
  currentProduct: Product | null = null;
  searchResults: Product[] = [];
  searchSuggestionResults: Product[] = [];
  searchSuggestionTerms: string[] = [];
  searchSource: string | null = null;
  searchSuggestionRequestSeq = 0;
  searchResultsRequestSeq = 0;
  recommendedProducts: Product[] = [];
  relatedProducts: Product[] = [];
  shopProducts: Product[] = [];
  shopProductsError: string | null = null;
  productCollections: ApiHomepageCollections | null = null;

  // New state for missing features
  productFilters: ApiFiltersResponse | null = null;
  categoryNavigation: ApiCategoriesResponse['results'] = [];
  categoryHierarchy: ApiCategoriesResponse['results'] = [];
  homepageCollections: ApiHomepageCollections['sections'] = [];
  collectionLoading: boolean = false;
  brands: Brand[] = [];
  isLoadingBrands = false;
  marketplaceHeroBanners: MarketplaceHeroBanner[] = [];
  marketplaceAnnouncementStrips: MarketplaceAnnouncementStrip[] = [];
  marketplaceShowcaseCards: MarketplaceShowcaseCard[] = [];
  programRewardOffers: ProgramRewardOffer[] = [];
  isLoadingProgramRewardOffers = false;
  hasLoadedProgramRewardOffers = false;
  programRewardOffersRetryCount = 0;
  programRewardOffersRetryExhausted = false;
  private programRewardOffersRequestVersion = 0;
  isLoadingMarketplaceHomeContent = false;
  hasLoadedMarketplaceHomeContent = false;
  marketplaceHomeContentRetryCount = 0;
  marketplaceHomeContentRetryExhausted = false;
  private hasLoadedMarketplaceHeroBanners = false;
  private hasLoadedMarketplaceAnnouncementStrips = false;
  private hasLoadedMarketplaceShowcaseCards = false;
  private marketplaceHomeContentLoadPromise: Promise<void> | null = null;
  // Payment-related state
  paymentOptions: PaymentOption[] = [];
  pssPlans: PSSPlansResponse | null = null;
  isLoadingPaymentOptions = false;
  isLoadingPSSPlans = false;
  private paymentOptionsRequestVersion = 0;
  private shopProductsRequestVersion = 0;

  isLoading = false;
  isSearching = false;
  isSearchingSuggestions = false;
  error: string | null = null;
  productLoadStatusCode: number | null = null;
  paymentOptionsError: ProductStoreApiError | null = null;
  searchQuery = '';
  searchError: string | null = null;
  currentFilters: ProductFiltersRequest = {};

  // Category page specific state
  categoryProducts: Product[] = [];
  categoryProductTotalCount = 0;
  categoryHasMoreProducts = true;
  categoryCurrentPage = 0;
  categorySelectedPriceRange = '--';
  categorySelectedBrand = '--';
  categorySelectedSort = '--';
  categorySelectedTags: string[] = [];
  isLoadingCategory = false;
  categoryLoadedKey: string | null = null;
  categoryError: string | null = null;
  private categoryRequestVersion = 0;
  private categoryRequestKey = '';

  constructor(
    private readonly getProductsUseCase: GetProductsUseCase,
    private readonly getPaymentPlansUseCase: GetPaymentPlansUseCase,
    private readonly productRepository: ApiProductRepository
  ) {
    makeAutoObservable(this);
  }

  // Computed values
  get hasSearchResults(): boolean {
    return this.searchResults.length > 0;
  }

  get hasSearchSuggestions(): boolean {
    return this.searchSuggestionResults.length > 0;
  }

  get hasProducts(): boolean {
    return this.products.length > 0;
  }

  get hasFeaturedProducts(): boolean {
    return this.featuredProducts.length > 0;
  }

  get hasRecommendedProducts(): boolean {
    return this.recommendedProducts.length > 0;
  }

  get hasRelatedProducts(): boolean {
    return this.relatedProducts.length > 0;
  }

  get isProductLoaded(): boolean {
    return this.currentProduct !== null;
  }

  get availableProducts(): Product[] {
    return this.products.filter((product) => product.isAvailable());
  }

  get featuredAvailableProducts(): Product[] {
    return this.featuredProducts.filter((product) => product.isAvailable());
  }

  // Payment-related computed values
  get hasPaymentOptions(): boolean {
    return this.paymentOptions.length > 0;
  }

  get hasPSSPlans(): boolean {
    return this.pssPlans !== null;
  }

  get isPSSAvailable(): boolean {
    return (
      this.paymentOptions?.some(
        (option: PaymentOption) =>
          option.type === 'pay_small_small' && option.available
      ) || false
    );
  }

  get isPSSCreditAvailable(): boolean {
    return (
      this.paymentOptions?.some(
        (option: PaymentOption) =>
          option.type === 'pay_small_small' &&
          option.available &&
          option.pss_credit_eligibility?.eligible === true
      ) || false
    );
  }

  get isUserEligibleForPSS(): boolean {
    return this.pssPlans?.userEligibility?.pssCreditEligible || false;
  }

  get recommendedPSSPlan(): PSSDurationOption | null {
    if (!this.pssPlans) return null;

    const recommendedDuration =
      this.pssPlans.userEligibility.recommendedDuration;
    return this.pssPlans.durationOptions.find(
      (option: PSSDurationOption) => option.months === recommendedDuration
    ) ?? null;
  }

  get availablePaymentMethods(): PaymentOption[] {
    return this.paymentOptions.filter(
      (option: PaymentOption) => option.available
    );
  }

  // New computed values for missing features
  get hasProductFilters(): boolean {
    return this.productFilters !== null;
  }

  get hasCategoryNavigation(): boolean {
    return this.categoryNavigation.length > 0;
  }

  get hasCategoryHierarchy(): boolean {
    return this.categoryHierarchy !== null;
  }

  get hasHomepageCollections(): boolean {
    return this.homepageCollections !== null;
  }

  get hasBrands(): boolean {
    return this.brands.length > 0;
  }

  get hasMarketplaceHeroBanners(): boolean {
    return this.marketplaceHeroBanners.length > 0;
  }

  get hasMarketplaceShowcaseCards(): boolean {
    return this.marketplaceShowcaseCards.length > 0;
  }

  get hasMarketplaceAnnouncementStrips(): boolean {
    return this.marketplaceAnnouncementStrips.length > 0;
  }

  get hasProgramRewardOffers(): boolean {
    return this.programRewardOffers.length > 0;
  }

  get availableCategories(): ApiCategoriesResponse['results'] {
    return (
      this.categoryNavigation.filter(
        (category) => category.is_featured_menu
      ) || []
    );
  }

  resolveCategoryDisplayTitle(categoryKey: string, fallbackTitle: string): string {
    const trimmedKey = categoryKey.trim();
    if (!trimmedKey) {
      return fallbackTitle;
    }

    const knownCategory = this.findKnownCategoryByKey(trimmedKey);
    if (knownCategory?.name) {
      return knownCategory.name;
    }

    const productCategory = this.categoryProducts.find((product) => {
      const category = product.category;
      return (
        category.id.toLowerCase() === trimmedKey.toLowerCase() ||
        category.slug.toLowerCase() === trimmedKey.toLowerCase()
      );
    })?.category;

    return productCategory?.name || fallbackTitle;
  }

  get getProductRanges() {
    return this.productFilters?.price_ranges || [];
  }

  get productPriceRange(): { min: number; max: number } {
    if (
      !this.productFilters?.price_ranges ||
      this.productFilters.price_ranges.length === 0
    ) {
      return { min: 0, max: 10000 };
    }

    // Find the overall min and max from all price ranges
    const min = Math.min(...this.productFilters.price_ranges.map((p) => p.min));
    const max = Math.max(...this.productFilters.price_ranges.map((p) => p.max));

    return { min, max };
  }

  get availableBrands(): string[] {
    // Backend returns brands as an array of strings (brand names only)
    return this.productFilters?.brands || [];
  }


  get hasCategoryFiltersApplied(): boolean {
    return (
      this.categorySelectedPriceRange !== '--' ||
      this.categorySelectedBrand !== '--' ||
      this.categorySelectedSort !== '--' ||
      this.categorySelectedTags.length > 0
    );
  }

  // Actions
  async loadProducts(filters?: ProductFiltersRequest): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const products = await this.getProductsUseCase.getAll(filters);

      runInAction(() => {
        this.products = products;
        this.currentFilters = filters || {};
      });
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to load products'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadFeaturedProducts(limit: number = 10): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const products = await this.getProductsUseCase.getFeatured(limit);

      runInAction(() => {
        this.featuredProducts = products;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load featured products'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadProductById(id: string): Promise<void> {
    this.setLoading(true);
    this.setError(null);
    runInAction(() => {
      this.productLoadStatusCode = null;
    });

    try {
      const product = await this.getProductsUseCase.getById(id);

      runInAction(() => {
        this.currentProduct = product;
      });
    } catch (error) {
      const httpError = error as HttpErrorLike;
      const statusCode =
        typeof httpError.response?.status === 'number'
          ? httpError.response.status
          : error instanceof Error && error.message === 'Product not found'
            ? 404
          : null;
      runInAction(() => {
        this.productLoadStatusCode = statusCode;
      });
      this.setError(
        statusCode === 404
          ? 'Product not found'
          : error instanceof Error
            ? error.message
            : 'Failed to load product'
      );
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async setProductFavoriteStatus(
    productId: string,
    shouldFavorite: boolean
  ): Promise<boolean> {
    const isFavorite = await this.productRepository.setFavoriteStatus(
      productId,
      shouldFavorite
    );

    runInAction(() => {
      this.applyFavoriteState(productId, isFavorite);
    });

    return isFavorite;
  }

  async loadProductsByCategory(
    categoryId: string,
    filters?: ProductFiltersRequest
  ): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const products = await this.getProductsUseCase.getByCategory(
        categoryId,
        filters
      );

      runInAction(() => {
        this.products = products.results;
        this.currentFilters = { ...filters, categories: [categoryId] };
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load products by category'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadProductsByShop(
    shopId: string,
    filters?: ProductFiltersRequest
  ): Promise<void> {
    const requestVersion = ++this.shopProductsRequestVersion;
    this.shopProducts = [];
    this.shopProductsError = null;

    try {
      const products = await this.getProductsUseCase.getByShop(shopId, filters);

      runInAction(() => {
        if (requestVersion !== this.shopProductsRequestVersion) {
          return;
        }
        this.shopProducts = products;
      });
    } catch (error) {
      runInAction(() => {
        if (requestVersion !== this.shopProductsRequestVersion) {
          return;
        }
        this.shopProducts = [];
        this.shopProductsError =
          error instanceof Error ? error.message : 'Failed to load merchant products';
      });
    }
  }

  async searchProducts(request: ProductSearchRequest): Promise<void> {
    const requestId = ++this.searchResultsRequestSeq;
    const normalizedQuery = request.query.trim();
    this.setSearching(true);
    this.setError(null);
    runInAction(() => {
      this.searchQuery = normalizedQuery;
      this.searchError = null;
      this.searchResults = [];
      this.currentFilters = request.filters || {};
    });

    try {
      const products = await this.getProductsUseCase.search(
        normalizedQuery,
        request.filters
      );

      runInAction(() => {
        if (requestId !== this.searchResultsRequestSeq) {
          return;
        }
        this.searchResults = products;
      });
    } catch (error) {
      runInAction(() => {
        if (requestId !== this.searchResultsRequestSeq) {
          return;
        }
        this.searchError =
          error instanceof Error ? error.message : 'Failed to search products';
        this.error = this.searchError;
      });
    } finally {
      runInAction(() => {
        if (requestId !== this.searchResultsRequestSeq) {
          return;
        }
        this.setSearching(false);
      });
    }
  }

  async loadSearchSuggestions(request: ProductSearchRequest): Promise<void> {
    this.setSearchingSuggestions(true);
    const requestId = ++this.searchSuggestionRequestSeq;
    try {
      const result = await this.getProductsUseCase.searchSuggestions(
        request.query,
        request.filters
      );

      runInAction(() => {
        if (requestId !== this.searchSuggestionRequestSeq) {
          return;
        }
        this.searchSuggestionResults = result.products;
        this.searchSuggestionTerms = result.suggestions || [];
        this.searchSource = result.source || null;
      });
    } catch (_error) {
      runInAction(() => {
        if (requestId !== this.searchSuggestionRequestSeq) {
          return;
        }
        this.searchSuggestionResults = [];
        this.searchSuggestionTerms = [];
        this.searchSource = null;
      });
    } finally {
      runInAction(() => {
        if (requestId === this.searchSuggestionRequestSeq) {
          this.setSearchingSuggestions(false);
        }
      });
    }
  }

  async loadRecommendedProducts(
    userId: string,
    limit: number = 10
  ): Promise<void> {
    try {
      const products = await this.getProductsUseCase.getRecommendations(
        userId,
        limit
      );

      runInAction(() => {
        this.recommendedProducts = products;
      });
    } catch (_error) {
      // Best-effort: recommended products should not block the page.
    }
  }

  async loadRelatedProducts(
    productId: string,
    limit: number = 5
  ): Promise<void> {
    try {
      const products = await this.getProductsUseCase.getRelated(
        productId,
        limit
      );

      runInAction(() => {
        this.relatedProducts = products;
      });
    } catch (_error) {
      // Best-effort: related products should not block the page.
    }
  }

  // async loadProductCollections(): Promise<void> {
  //   this.setLoading(true);
  //   this.setError(null);
  //
  //   try {
  //     const collections = await this.productRepository.findCollections();
  //
  //     runInAction(() => {
  //       this.productCollections = collections;
  //     });
  //   } catch (error) {
  //     this.setError(
  //       error instanceof Error
  //         ? error.message
  //         : 'Failed to load product collections'
  //     );
  //   } finally {
  //     this.setLoading(false);
  //   }
  // }

  async loadProductFilters(): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const filters = await this.productRepository.getProductFilters?.();
      runInAction(() => {
        this.productFilters = filters ?? null;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load product filters'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadCategoryNavigation(): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const navigation = await this.productRepository.getCategoryNavigation();

      runInAction(() => {
        this.categoryNavigation = navigation.results || navigation;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load category navigation'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async getFeaturedCategories(isFeatured: boolean = true): Promise<boolean> {
    this.setLoading(true);
    this.setError(null);

    try {
      const hierarchy = await this.productRepository.getFeaturedCategories(
        isFeatured
      );

      runInAction(() => {
        this.categoryHierarchy = hierarchy.results;
      });
      return true;
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load category hierarchy'
      );
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  async loadHomepageCollections(): Promise<void> {
    this.setLoading(true);
    this.setError(null);
    this.setCollectionLoading(true);
    try {
      const collections = await this.productRepository.getHomepageCollections();

      runInAction(() => {
        // The backend owns merchandising order, including deliberate cross-row overlap.
        this.homepageCollections = collections.sections.filter(
          (section) => (section.results ?? []).length > 0
        );
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load homepage collections'
      );
    } finally {
      this.setLoading(false);
      this.setCollectionLoading(false);
    }
  }

  async loadBrands(limit: number = 20): Promise<void> {
    this.isLoadingBrands = true;

    try {
      const [response, catalogueFilters] = await Promise.all([
        this.productRepository.getBrands({ limit, is_active: true }),
        this.productRepository.getProductFilters(),
      ]);
      // The public catalogue's filters exclude brands with no visible products.
      // Keep the full brand records for their genuine logos and stable URLs.
      const availableIds = new Set(catalogueFilters.brand_ids ?? []);

      runInAction(() => {
        this.brands = response.results.filter(brand =>
          brand.is_active && availableIds.has(brand.id)
        );
      });
    } catch (_error) {
      // Best-effort: brands section should not block the page
    } finally {
      runInAction(() => {
        this.isLoadingBrands = false;
      });
    }
  }

  private getMarketplaceHomeContentRetryDelayMs(): number {
    if (this.marketplaceHomeContentRetryCount <= 0) {
      return 0;
    }

    // Exponential backoff keeps retries moving forward without hammering the API.
    const baseDelayMs = 1000;
    const maxDelayMs = 8000;
    const exponent = this.marketplaceHomeContentRetryCount - 1;

    return Math.min(maxDelayMs, baseDelayMs * 2 ** exponent);
  }

  private async waitForMarketplaceHomeContentRetry(delayMs: number): Promise<void> {
    if (delayMs <= 0) {
      return;
    }

    await new Promise<void>((resolve) => {
      setTimeout(resolve, delayMs);
    });
  }

  private getMarketplaceHomeContentLoaders(): Array<{
    key: 'hero' | 'announcement' | 'showcase';
    request: () => Promise<HomeContentLoaderResponse<unknown>>;
  }> {
    const loaders: Array<{
      key: 'hero' | 'announcement' | 'showcase';
      request: () => Promise<HomeContentLoaderResponse<unknown>>;
    }> = [];

    if (!this.hasLoadedMarketplaceHeroBanners) {
      loaders.push({
        key: 'hero',
        request: () => this.productRepository.getMarketplaceHeroBanners(),
      });
    }

    if (!this.hasLoadedMarketplaceAnnouncementStrips) {
      loaders.push({
        key: 'announcement',
        request: () => this.productRepository.getMarketplaceAnnouncementStrips(),
      });
    }

    if (!this.hasLoadedMarketplaceShowcaseCards) {
      loaders.push({
        key: 'showcase',
        request: () => this.productRepository.getMarketplaceShowcaseCards(),
      });
    }

    return loaders;
  }

  async loadMarketplaceHomeContent(): Promise<void> {
    if (this.marketplaceHomeContentRetryExhausted) {
      return;
    }

    if (this.hasLoadedMarketplaceHomeContent) {
      return;
    }

    if (this.marketplaceHomeContentLoadPromise) {
      return this.marketplaceHomeContentLoadPromise;
    }

    const loadPromise = (async () => {
      runInAction(() => {
        this.isLoadingMarketplaceHomeContent = true;
      });

      try {
        const pendingLoaders = this.getMarketplaceHomeContentLoaders();

        if (pendingLoaders.length === 0) {
          runInAction(() => {
            this.hasLoadedMarketplaceHomeContent = true;
            this.marketplaceHomeContentRetryCount = 0;
          });
          return;
        }

        await this.waitForMarketplaceHomeContentRetry(
          this.getMarketplaceHomeContentRetryDelayMs()
        );

        const responses = await Promise.allSettled(
          pendingLoaders.map((loader) => loader.request())
        );

        runInAction(() => {
          let hadFailure = false;

          responses.forEach((response, index) => {
            const loader = pendingLoaders[index];
            if (!loader) return;

            if (response.status !== 'fulfilled') {
              hadFailure = true;
              return;
            }

            if (loader.key === 'hero') {
              this.marketplaceHeroBanners =
                (response.value?.results as MarketplaceHeroBanner[] | undefined) || [];
              this.hasLoadedMarketplaceHeroBanners = true;
              return;
            }

            if (loader.key === 'announcement') {
              this.marketplaceAnnouncementStrips =
                (response.value?.results as MarketplaceAnnouncementStrip[] | undefined) || [];
              this.hasLoadedMarketplaceAnnouncementStrips = true;
              return;
            }

            this.marketplaceShowcaseCards =
              (response.value?.results as MarketplaceShowcaseCard[] | undefined) || [];
            this.hasLoadedMarketplaceShowcaseCards = true;
          });

          const loadedAll =
            this.hasLoadedMarketplaceHeroBanners &&
            this.hasLoadedMarketplaceAnnouncementStrips &&
            this.hasLoadedMarketplaceShowcaseCards;

          this.hasLoadedMarketplaceHomeContent = loadedAll;
          this.marketplaceHomeContentRetryCount = loadedAll
            ? 0
            : hadFailure
              ? this.marketplaceHomeContentRetryCount + 1
              : this.marketplaceHomeContentRetryCount;

          if (this.marketplaceHomeContentRetryCount >= MAX_MARKETPLACE_HOME_CONTENT_RETRIES) {
            this.marketplaceHomeContentRetryExhausted = true;
          }
        });
      } catch (_error) {
        // Best-effort: homepage sections should not block catalog rendering.
        runInAction(() => {
          this.marketplaceHomeContentRetryCount += 1;

          if (this.marketplaceHomeContentRetryCount >= MAX_MARKETPLACE_HOME_CONTENT_RETRIES) {
            this.marketplaceHomeContentRetryExhausted = true;
          }
        });
      } finally {
        runInAction(() => {
          this.isLoadingMarketplaceHomeContent = false;
          this.marketplaceHomeContentLoadPromise = null;
        });
      }
    })();

    this.marketplaceHomeContentLoadPromise = loadPromise;
    return loadPromise;
  }

  async loadProgramRewardOffers(force: boolean = false): Promise<void> {
    if (this.isLoadingProgramRewardOffers) {
      return;
    }
    if (this.hasLoadedProgramRewardOffers && !force) {
      return;
    }
    if (this.programRewardOffersRetryExhausted && !force) {
      return;
    }
    if (!this.productRepository.getProgramRewardOffers) {
      runInAction(() => {
        this.programRewardOffers = [];
        this.hasLoadedProgramRewardOffers = true;
      });
      return;
    }

    runInAction(() => {
      this.isLoadingProgramRewardOffers = true;
    });
    const requestVersion = ++this.programRewardOffersRequestVersion;

    try {
      const response = await this.productRepository.getProgramRewardOffers();
      runInAction(() => {
        if (requestVersion !== this.programRewardOffersRequestVersion) {
          return;
        }
        this.programRewardOffers = Array.isArray(response?.results)
          ? response.results
          : [];
        this.hasLoadedProgramRewardOffers = true;
        this.programRewardOffersRetryCount = 0;
        this.programRewardOffersRetryExhausted = false;
      });
    } catch (error) {
      const httpError = error as HttpErrorLike;
      const statusCode =
        typeof httpError.response?.status === 'number'
          ? httpError.response.status
          : undefined;
      const isUnauthorizedOrForbidden = statusCode === 401 || statusCode === 403;
      runInAction(() => {
        if (requestVersion !== this.programRewardOffersRequestVersion) {
          return;
        }
        this.programRewardOffers = [];
        if (isUnauthorizedOrForbidden) {
          // Treat explicit 401/403 as a terminal empty state for this page load.
          // The header should not keep hammering reward-offer fetches when auth
          // is denied; users simply see no reward strip.
          this.hasLoadedProgramRewardOffers = true;
          this.programRewardOffersRetryCount = 0;
          this.programRewardOffersRetryExhausted = false;
          return;
        }
        this.hasLoadedProgramRewardOffers = false;
        this.programRewardOffersRetryCount += 1;
        this.programRewardOffersRetryExhausted =
          this.programRewardOffersRetryCount >= MAX_PROGRAM_REWARD_OFFER_RETRIES;
      });
    } finally {
      runInAction(() => {
        if (requestVersion !== this.programRewardOffersRequestVersion) {
          return;
        }
        this.isLoadingProgramRewardOffers = false;
      });
    }
  }

  clearProgramRewardOffers(): void {
    this.programRewardOffersRequestVersion += 1;
    this.programRewardOffers = [];
    this.hasLoadedProgramRewardOffers = false;
    this.isLoadingProgramRewardOffers = false;
    this.programRewardOffersRetryCount = 0;
    this.programRewardOffersRetryExhausted = false;
  }

  async loadPaymentOptions(productId: string): Promise<void> {
    const requestVersion = ++this.paymentOptionsRequestVersion;
    this.isLoadingPaymentOptions = true;
    this.paymentOptionsError = null;

    try {
      const options = await this.productRepository.getPaymentOptions(productId);
      if (requestVersion !== this.paymentOptionsRequestVersion) {
        return;
      }

      runInAction(() => {
        this.paymentOptions = options.payment_options;
      });
    } catch (error) {
      if (requestVersion !== this.paymentOptionsRequestVersion) {
        return;
      }
      const httpError = error as HttpErrorLike;
      const responseData = httpError.response?.data;
      const statusCode =
        typeof httpError.response?.status === 'number'
          ? httpError.response.status
          : undefined;
      const message =
        typeof responseData?.message === 'string'
          ? responseData.message
          : error instanceof Error
            ? error.message
            : 'Failed to load payment options';

      runInAction(() => {
        this.paymentOptions = [];
        this.paymentOptionsError = {
          message,
          status: statusCode,
          code:
            typeof responseData?.code === 'string' ? responseData.code : undefined,
          details:
            responseData?.details && typeof responseData.details === 'object'
              ? responseData.details
              : undefined,
        };
      });
    } finally {
      runInAction(() => {
        if (requestVersion === this.paymentOptionsRequestVersion) {
          this.isLoadingPaymentOptions = false;
        }
      });
    }
  }

  async loadPSSPlans(productId: string, userId: string): Promise<void> {
    this.isLoadingPSSPlans = true;
    this.setError(null);

    try {
      const plans = await this.getPaymentPlansUseCase.execute({
        productId,
        userId,
      });

      runInAction(() => {
        this.pssPlans = plans;
      });
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to load PSS plans'
      );
      throw error;
    } finally {
      runInAction(() => {
        this.isLoadingPSSPlans = false;
      });
    }
  }

  // Helper methods
  setCurrentProduct(product: Product | null): void {
    this.currentProduct = product;
    this.productLoadStatusCode = null;
    this.invalidatePaymentOptionsRequest();
    this.pssPlans = null;
  }

  clearPaymentData(): void {
    this.paymentOptions = [];
    this.paymentOptionsError = null;
    this.pssPlans = null;
  }

  private applyFavoriteState(productId: string, isFavorite: boolean): void {
    const withFavoriteState = (product: Product): Product =>
      product.id === productId
        ? Product.fromObject({ ...product, is_favorite: isFavorite } as any)
        : product;

    if (this.currentProduct?.id === productId) {
      this.currentProduct = withFavoriteState(this.currentProduct);
    }

    this.products = this.products.map(withFavoriteState);
    this.featuredProducts = this.featuredProducts.map(withFavoriteState);
    this.searchResults = this.searchResults.map(withFavoriteState);
    this.searchSuggestionResults = this.searchSuggestionResults.map(withFavoriteState);
    this.recommendedProducts = this.recommendedProducts.map(withFavoriteState);
    this.relatedProducts = this.relatedProducts.map(withFavoriteState);
    this.shopProducts = this.shopProducts.map(withFavoriteState);
    this.categoryProducts = this.categoryProducts.map(withFavoriteState);
  }

  /**
   * Cancel/ignore any in-flight payment option requests.
   *
   * Some UI flows (variant switches) intentionally avoid fetching payment options
   * for unsellable/out-of-stock states. In those cases, we still need to bump the
   * request version so a previous request cannot overwrite state for the new
   * selection when it resolves late.
   */
  invalidatePaymentOptionsRequest(): void {
    this.paymentOptionsRequestVersion += 1;
    this.isLoadingPaymentOptions = false;
    this.paymentOptions = [];
    this.paymentOptionsError = null;
  }

  calculateMonthlyPayment(
    productPrice: number,
    durationMonths: number,
    kmpEnabled: boolean = false
  ): number {
    let baseAmount = productPrice / durationMonths;

    if (kmpEnabled && this.pssPlans?.kmpPolicy) {
      const kmpFee =
        (productPrice * this.pssPlans.kmpPolicy.basePercentage) / 100;
      baseAmount += kmpFee / durationMonths;
    }

    return baseAmount;
  }

  getPSSPlanByDuration(months: number): PSSDurationOption | undefined {
    return this.pssPlans?.durationOptions?.find(
      (option) => option.months === months
    );
  }

  getProductCollectionByKey(
    key: string
  ): ApiHomepageCollections['sections'][number] | undefined {
    return this.productCollections?.sections?.find(
      (section) => section.key === key
    );
  }

  clearSearchResults(): void {
    this.searchResultsRequestSeq += 1;
    this.searchSuggestionRequestSeq += 1;
    this.searchResults = [];
    this.searchSuggestionResults = [];
    this.searchSuggestionTerms = [];
    this.searchSource = null;
    this.searchQuery = '';
    this.searchError = null;
    this.isSearching = false;
  }

  clearSearchSuggestions(): void {
    this.searchSuggestionRequestSeq += 1;
    this.searchSuggestionResults = [];
    this.searchSuggestionTerms = [];
    this.searchSource = null;
    this.isSearchingSuggestions = false;
  }

  clearFilters(): void {
    this.currentFilters = {};
  }

  getProductsByPriceRange(min: number, max: number): Product[] {
    return this.products.filter(
      (product) => product.retail_price >= min && product.retail_price <= max
    );
  }

  // getProductsByCategory(category: string): Product[] {
  //   return this.products.filter((product) =>
  //     product.category.name.includes(category)
  //   );
  // }

  // Category page specific methods
  setCategoryFilter(
    filterType: 'priceRange' | 'brand' | 'sort' | 'tags',
    value: string | string[]
  ): void {
    runInAction(() => {
      switch (filterType) {
        case 'priceRange':
          this.categorySelectedPriceRange = value as string;
          break;
        case 'brand':
          this.categorySelectedBrand = value as string;
          break;
        case 'sort':
          this.categorySelectedSort = value as string;
          break;
        case 'tags':
          this.categorySelectedTags = value as string[];
          break;
      }
    });
  }

  clearCategoryFilters(): void {
    runInAction(() => {
      this.categorySelectedPriceRange = '--';
      this.categorySelectedBrand = '--';
      this.categorySelectedSort = '--';
      this.categorySelectedTags = [];
    });
  }

  async loadCategoryProducts(
    categories: string[],
    reset = false
  ): Promise<void> {
    const requestedPage = reset ? 1 : this.categoryCurrentPage + 1;
    const filters = this.buildCategoryFilters(categories, requestedPage);
    const requestKey = JSON.stringify(filters);
    if (this.isLoadingCategory && (!reset || requestKey === this.categoryRequestKey)) return;
    const requestVersion = ++this.categoryRequestVersion;
    this.categoryRequestKey = requestKey;

    this.isLoadingCategory = true;
    this.categoryError = null;
    this.setError(null);

    try {
      if (reset) {
        this.categoryLoadedKey = null;
        this.categoryProducts = [];
        this.categoryProductTotalCount = 0;
        this.categoryHasMoreProducts = true;
        this.categoryCurrentPage = 0;
      }

      const result = await this.getProductsUseCase.getByCategory(
        categories[0] || '',
        filters
      );

      runInAction(() => {
        if (requestVersion !== this.categoryRequestVersion) return;
        if (reset) {
          this.categoryProducts = result.results;
        } else {
          const existingIds = new Set(this.categoryProducts.map((p) => p.id));
          const newProducts = result.results.filter(
            (p) => !existingIds.has(p.id)
          );
          this.categoryProducts = [...this.categoryProducts, ...newProducts];
        }

        this.categoryProductTotalCount =
          result.count ?? this.categoryProducts.length;
        this.categoryHasMoreProducts = Boolean(result.next);
        this.categoryCurrentPage = requestedPage;
        this.categoryLoadedKey = categories[0] || '';
      });
    } catch (error) {
      if (requestVersion !== this.categoryRequestVersion) return;
      runInAction(() => {
        this.categoryError = error instanceof Error
          ? error.message
          : 'Failed to load category products';
        this.setError(this.categoryError);
      });
    } finally {
      runInAction(() => {
        if (requestVersion !== this.categoryRequestVersion) return;
        this.isLoadingCategory = false;
      });
    }
  }

  async reloadCategoryWithFilters(categories: string[]): Promise<void> {
    await this.loadCategoryProducts(categories, true);
  }

  async loadMoreCategoryProducts(categories: string[]): Promise<void> {
    if (this.isLoadingCategory || !this.categoryHasMoreProducts) return;
    await this.loadCategoryProducts(categories, false);
  }

  private setLoading(loading: boolean): void {
    runInAction(() => {
      this.isLoading = loading;
    });
  }
  private setCollectionLoading(loading: boolean): void {
    runInAction(() => {
      this.collectionLoading = loading;
    });
  }

  private findKnownCategoryByKey(
    categoryKey: string
  ): ApiCategoriesResponse['results'][number] | null {
    const normalizedKey = categoryKey.toLowerCase();
    const stack = [...this.categoryNavigation, ...this.categoryHierarchy];

    while (stack.length > 0) {
      const category = stack.shift();
      if (!category) {
        continue;
      }

      if (
        category.id.toLowerCase() === normalizedKey ||
        category.slug.toLowerCase() === normalizedKey
      ) {
        return category;
      }

      if (Array.isArray(category.children)) {
        stack.push(...category.children);
      }
    }

    return null;
  }

  private setSearching(searching: boolean): void {
    this.isSearching = searching;
  }

  private setSearchingSuggestions(searching: boolean): void {
    this.isSearchingSuggestions = searching;
  }

  private setError(error: string | null): void {
    this.error = error;
  }

  private buildCategoryFilters(
    categories: string[],
    page: number
  ): ProductFilters {
    const filters: ProductFilters = {
      categories: categories.map((cat) => cat.toLowerCase()),
      includeDescendants: true,
      page,
    };

    // Add price range filter
    if (this.categorySelectedPriceRange !== '--') {
      const [min, max] = this.categorySelectedPriceRange.split('-').map(Number);
      filters.minPrice = min;
      filters.maxPrice = max;
    }

    // Add brand filter
    if (this.categorySelectedBrand !== '--') {
      filters.brandName = this.categorySelectedBrand;
    }

    // Add tag filters
    if (this.categorySelectedTags.length > 0) {
      filters.tags = this.categorySelectedTags;
    }

    // Add sorting
    if (this.categorySelectedSort !== '--') {
      if (this.categorySelectedSort === 'lowprice') {
        filters.sortBy = 'price';
        filters.sortOrder = 'asc';
      } else if (this.categorySelectedSort === 'highprice') {
        filters.sortBy = 'price';
        filters.sortOrder = 'desc';
      } else if (this.categorySelectedSort === 'name') {
        filters.sortBy = 'name';
        filters.sortOrder = 'asc';
      }
    }

    return filters;
  }
}

export interface NavigationCategory {
  id: string;
  slug: string;
  name: string;
  icon_url?: string;
  product_count?: number;
  sort_order?: number;
  is_featured: boolean;
  is_featured_menu: boolean; // New field for top nav curation
  category_id?: string; // Legacy support
  category_name?: string; // Legacy support
}

export interface NavigationHierarchy extends NavigationCategory {
  parent_id?: string | null;
  banner_image_url?: string;
  children?: NavigationHierarchy[];
}

export interface HomeCollection {
  key: string;
  title: string;
  description: string;
  products: Product[];
}
